import React, { ReactNode, useEffect, useState } from 'react';
import AgentationDevtools from '@/components/AgentationDevtools';
import { getEnv, navigate, pageAgent } from '@/common/agents';
import { message } from '$/components/Message';
import { useLanguageStore } from '$/store/languageStore';
import RoboneoContext from '$/pages/RoboneoContext';
import { useOfficialStore, InitLoginConfig } from '$/store/useOfficialStore';
import { isMobile } from '$/utils';
import { NoSSR } from '@meitu/catalyze-next';
import CookieBanner from '@/components/CookieBanner';
import { useCommonStore } from '@/store/useCommonStore';
import { ensureTeamInfoForPurchase } from '@/domains/team/teamPurchaseGuard';
import type { VipMembershipMode } from '$/store/useVipModalStore';
import Navbar from '@/components/Navbar';
import { PageInfo } from '@/common/pageType';
import Header from '@/components/Header';
// import MetaPixelSDK from '@/components/MetaPixelSDK';
import { MetaPixelProvider } from '@/components/MetaPixelsdk';
import styles from './index.module.scss';

function Layout({ children }: { children: ReactNode; }) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const isOversea = useOfficialStore((state) => state.isOversea());
  const toLogin = useCommonStore((state) => state.toLogin);
  const currentPathname = useCommonStore((state) => state.currentPathname);
  // const currentCountryCode = useOfficialStore((state) => state.currentCountryCode);
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const init = useCommonStore((state) => state.init);
  const initialized = useCommonStore((state) => state.initialized);
  const disposeOfflineListener = useCommonStore(
    (state) => state.disposeOfflineListener,
  );
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const { locationName } = pageAgent.getCurrentPage() || {};

  const loadVconsole = async () => {
    if (isMobile() && process.env.NEXT_PUBLIC_ENVIRONMENT !== 'release') {
      const VConsole = await import('vconsole');
      // eslint-disable-next-line no-new, new-cap
      new VConsole.default();
    }
  };

  useEffect(() => {
    InitLoginConfig({
      toLogin: async () => {
        await toLogin();
        initUserInfo();
      },
    });
    init();
    loadVconsole();
  }, []);

  /**
   * 团队版购买前确保已有团队；没有团队时创建默认团队并让 SDK 使用新团队上下文下单。
   */
  const handleCreateQrcodeBefore = async ({ membershipMode }: { membershipMode: VipMembershipMode; }) => {
    if (membershipMode !== 'team') {
      return true;
    }

    const teamInfo = await ensureTeamInfoForPurchase();
    if (!teamInfo) {
      message.error(languageStore.smartcatTranslationText(
        (v) => v.current_team_info_unavailable_refresh_retry,
      ));
      return false;
    }

    return true;
  };
  // 注册chat的参数

  useEffect(() => {
    if (
      initialized
      && !isUserLoggedIn
      && locationName
      && (locationName === 'studio' || locationName === 'ai_flow')
    ) {
      message.info('RoboNeo_Web_login_check_text');
      navigate({
        mode: 'url',
        url: getEnv().ENV === 'development' ? '/--roboneo/home' : '/home',
        replace: true,
      });
    }
  }, [locationName, initialized, isUserLoggedIn]);

  const [activeNav, setActiveNav] = useState<PageInfo['locationName']>();
  const [showNavbar, setShowNavbar] = useState(false);
  const [hiddenHeader, setHiddenHeader] = useState(false);

  useEffect(() => {
    if (currentPathname) {
      const pageInfo = pageAgent.getCurrentPage();
      // 案例详情使用全屏沉浸式预览，不渲染全局左侧导航栏。
      const showGlobalNavbar = ['home', 'project', 'team_project', 'community', 'agent_shop', 'asset'].includes(pageInfo?.locationName || '');
      setShowNavbar(
        showGlobalNavbar,
      );
      setHiddenHeader(['case_detail'].includes(pageInfo?.locationName || ''));
      setActiveNav(pageInfo?.locationName);
    }
  }, [currentPathname]);
  /** 页面卸载时释放离线监听，Sentry 已由应用公共入口统一初始化。 */
  useEffect(() => () => {
    disposeOfflineListener();
  }, []);
  // useEffect(() => {
  //   // 初始化并收集参数
  //   const initializeAnalytics = async () => {
  //     // 等待 MetaPixelSDK 初始化
  //     await new Promise((resolve) => {
  //       const check = () => {
  //         if (window.MetaPixelSDK) {
  //           resolve();
  //         } else {
  //           setTimeout(check, 100);
  //         }
  //       };
  //       check();
  //     });

  //     // 收集 Facebook 参数
  //     const fbc = window.MetaPixelSDK.getFbc();
  //     if (fbc && window.SDK) {
  //       window.SDK.setCommonStatsParams('fb_fbc', fbc);
  //     }

  //     window.MetaPixelSDK.waitForFbqReady(() => {
  //       const fbp = window.MetaPixelSDK.getFbp();
  //       if (fbp && window.SDK) {
  //         window.SDK.setCommonStatsParams('fb_fbp', fbp);
  //       }
  //     });
  //   };

  //   initializeAnalytics();
  // }, []);

  return (
    <NoSSR>
      {getEnv().ENV !== 'release' && <AgentationDevtools />}
      {isOversea ? <CookieBanner /> : null}
      <div
        id="layout_container"
        className={styles.layout_container}
        // data-roboneo-theme={theme}
        style={{ background: 'var(--background-canvas)' }}
      >
        <MetaPixelProvider
          onReady={({ fbc, fbp }) => {
            if (fbc) window.mtstat?.setCommonParams({ fb_fbc: fbc });
            if (fbp) window.mtstat?.setCommonParams({ fb_fbp: fbp });
          }}
        >
          <RoboneoContext enableVip onCreateQrcodeBefore={handleCreateQrcodeBefore}>
            {/* 头部 */}
            {showNavbar && !hiddenHeader && <Header />}

            {/* 导航 */}
            {showNavbar && activeNav && (
            <Navbar activeNav={activeNav} setActiveNav={setActiveNav} />
            )}
            {/* Meta Pixel SDK 客户端组件 */}
            {/* <MetaPixelSDK /> */}
            {children as React.ReactNode}
          </RoboneoContext>
        </MetaPixelProvider>
      </div>
    </NoSSR>
  );
}
export default Layout;
